import { useCallback, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useMutation, useQuery } from '@apollo/client'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import { UPDATE_PROMO_CODE, GET_ONE_PROMO_CODE, REMOVE_PROMO_CODE } from 'queries/promoCode'
import { CreatePromocodeInput } from '__generatedTypes__/globalTypes'
import { updatePromoCode, updatePromoCodeVariables } from '__generatedTypes__/updatePromoCode'
import { findOnePromoCode, findOnePromoCodeVariables } from '__generatedTypes__/findOnePromoCode'
import { removePromoCode, removePromoCodeVariables } from '__generatedTypes__/removePromoCode'
import { findAllPlatforms_findAllPlatforms_items as Platform } from '__generatedTypes__/findAllPlatforms'
import withPlatformCheck from 'HOCs/withPlatformCheck'
import { PageLoader } from 'components/PageLoader'
import { toast } from 'utils/toast'
import { Form } from '../Form'

type Props = {
  platform: Platform
}

export const Edit = withPlatformCheck(({ platform }: Props) => {
  const { promoCodeId = '' } = useParams()
  const { navigate } = useLink()
  const [update] = useMutation<updatePromoCode, updatePromoCodeVariables>(UPDATE_PROMO_CODE)
  const [remove] = useMutation<removePromoCode, removePromoCodeVariables>(REMOVE_PROMO_CODE)
  const { data, loading } = useQuery<findOnePromoCode, findOnePromoCodeVariables>(GET_ONE_PROMO_CODE, {
    variables: {
      id: promoCodeId,
    },
    fetchPolicy: 'network-only',
  })
  const promoCode = data?.findOnePromocode

  const handleSubmit = useCallback(
    async (promoCodeInput: CreatePromocodeInput) => {
      if (!promoCode?.id) {
        return
      }

      try {
        const { data, errors } = await update({
          variables: {
            params: {
              id: promoCode.id,
              platform: {
                id: promoCode.platform.id,
              },
              ...promoCodeInput,
            },
          },
        })

        if (errors) {
          throw new Error(errors[0].message ?? 'Something went wrong!')
        }

        if (data?.updatePromocode.id) {
          navigate(cmsRoutes.PROMO_CODES_LIST)
          toast.success('Discount code updated successfully')
        } else {
          toast.error('Failed to update discount code')
        }
      } catch (error) {
        if ((error as Error)?.message?.includes('already exists')) {
          toast.error(`Discount code "${promoCodeInput.code}" already exists`)
          return
        }
        toast.error('Failed to update discount code')
      }
    },
    [update, navigate, promoCode]
  )

  const handleDelete = useCallback(async () => {
    try {
      const { data } = await remove({
        variables: {
          id: promoCodeId,
        },
      })

      if (data?.removePromocode) {
        navigate(cmsRoutes.PROMO_CODES_LIST)
      } else {
        toast.error('Failed to delete discount code')
      }
    } catch (error) {
      toast.error('Failed to delete discount code')
    }
  }, [promoCodeId, remove, navigate])

  useEffect(() => {
    if (!loading && !promoCode) {
      navigate(cmsRoutes.PROMO_CODES_LIST)
      toast.error('Discount code not found, redirecting to Discount codes list page')
    }
  }, [loading, promoCode])

  if (loading || !promoCode) {
    return <PageLoader />
  }

  return <Form platform={platform} promoCode={promoCode} onSubmit={handleSubmit} onDelete={handleDelete} />
})
